import React, { useState, useEffect, useCallback } from 'react';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Plus,
  Trash,
  ListBullets,
  Lightning,
} from '@phosphor-icons/react';
import type { Asset } from 'shared/types/asset';

interface ChildRecordType {
  id: number;
  recordType: string;
  displayName: string;
  schema: {
    fields: {
      name: string;
      label: string;
      type: 'number' | 'string' | 'date' | 'boolean';
      required: boolean;
      unit?: string;
    }[];
  };
}

interface ChildRecord {
  id: number;
  assetId: string;
  recordType: string;
  data: Record<string, any>;
  timestamp: string;
  source: string;
  createdAt: string;
  createdBy: string;
}

interface Props {
  entity: Asset;
  onUpdate: (updated: Asset) => void;
}

const AssetChildRecordsSection: React.FC<Props> = ({ entity: asset }) => {
  const { session } = useSession();
  const [recordTypes, setRecordTypes] = useState<ChildRecordType[]>([]);
  const [records, setRecords] = useState<ChildRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedRecordType, setSelectedRecordType] = useState<ChildRecordType | null>(null);

  // Fetch record types for this asset's category
  const fetchRecordTypes = useCallback(async () => {
    if (!session || !asset.assetCategoryId) return;
    try {
      const resp = await fetchWithAuth(
        `${API_URL}/assets/child-record-types?categoryId=${asset.assetCategoryId}`,
        { method: 'GET' },
        session,
      );
      if (resp.ok) setRecordTypes(await resp.json());
    } catch (err) {
      console.error('Fout bij het ophalen van recordtypes:', err);
    }
  }, [session, asset.assetCategoryId]);

  // Fetch records for this asset
  const fetchRecords = useCallback(async () => {
    if (!session) return;
    try {
      const limit = showAll ? 100 : 5;
      const resp = await fetchWithAuth(
        `${API_URL}/assets/${asset.id}/child-records?limit=${limit}`,
        { method: 'GET' },
        session,
      );
      if (resp.ok) {
        const data = await resp.json();
        setRecords(data.records);
        setTotal(data.total);
      }
    } catch (err) {
      console.error('Fout bij het ophalen van records:', err);
    }
  }, [session, asset.id, showAll]);

  useEffect(() => { fetchRecordTypes(); }, [fetchRecordTypes]);
  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const handleDelete = useCallback(async (recordId: number) => {
    if (!session) return;
    try {
      const resp = await fetchWithAuth(
        `${API_URL}/assets/child-records/${recordId}`,
        { method: 'DELETE' },
        session,
      );
      if (resp.ok) {
        toast.success('Record verwijderd');
        fetchRecords();
      } else {
        throw new Error('Verwijderen mislukt');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Fout bij het verwijderen');
    }
  }, [session, fetchRecords]);

  // Get display name for a record type
  const getTypeName = useCallback((recordType: string) => {
    return recordTypes.find(rt => rt.recordType === recordType)?.displayName || recordType;
  }, [recordTypes]);

  // Get schema for a record type
  const getSchema = useCallback((recordType: string) => {
    return recordTypes.find(rt => rt.recordType === recordType)?.schema;
  }, [recordTypes]);

  // Hide section only if no record types AND no existing records
  if (recordTypes.length === 0 && records.length === 0) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <ListBullets size={20} weight="duotone" className="text-blue-600" />
          <h3 className="text-md font-semibold">Registraties</h3>
          {total > 0 && (
            <Badge variant="secondary" className="text-xs">{total}</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {recordTypes.length === 1 ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setSelectedRecordType(recordTypes[0]); setAddDialogOpen(true); }}
              className="gap-1.5"
            >
              <Plus className="h-3.5 w-3.5" />
              <span className="text-xs">{recordTypes[0].displayName} toevoegen</span>
            </Button>
          ) : (
            recordTypes.map(rt => (
              <Button
                key={rt.id}
                variant="outline"
                size="sm"
                onClick={() => { setSelectedRecordType(rt); setAddDialogOpen(true); }}
                className="gap-1.5"
              >
                <Plus className="h-3.5 w-3.5" />
                <span className="text-xs">{rt.displayName}</span>
              </Button>
            ))
          )}
        </div>
      </div>

      {/* Records list */}
      {records.length === 0 ? (
        <div className="text-center py-8 text-sm text-muted-foreground border border-dashed rounded-lg">
          Nog geen registraties
        </div>
      ) : (
        <div className="space-y-1.5">
          {records.map(record => {
            const schema = getSchema(record.recordType);
            return (
              <div
                key={record.id}
                className="flex items-center gap-3 px-3 py-2 rounded-lg border bg-background hover:bg-muted/30 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium">
                      {getTypeName(record.recordType)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(record.timestamp).toLocaleDateString('nl-BE', {
                        day: 'numeric', month: 'short', year: 'numeric',
                      })}
                    </span>
                    {record.source && record.source !== 'manual' && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        <Lightning className="h-2.5 w-2.5 mr-0.5" />
                        {record.source}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {schema?.fields.map(field => {
                      const val = record.data[field.name];
                      if (val == null) return null;
                      return (
                        <span key={field.name}>
                          <span className="font-medium text-foreground/70">{field.label}:</span>{' '}
                          {String(val)}{field.unit ? ` ${field.unit}` : ''}
                        </span>
                      );
                    })}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                  onClick={() => handleDelete(record.id)}
                >
                  <Trash className="h-3.5 w-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Show more */}
      {total > 5 && !showAll && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-xs text-muted-foreground"
          onClick={() => setShowAll(true)}
        >
          Toon alle {total} registraties
        </Button>
      )}

      {/* Add record dialog */}
      {selectedRecordType && (
        <AddRecordDialog
          open={addDialogOpen}
          onClose={() => { setAddDialogOpen(false); setSelectedRecordType(null); }}
          recordType={selectedRecordType}
          assetId={asset.id}
          onCreated={fetchRecords}
        />
      )}
    </div>
  );
};

// --- Add Record Dialog ---

interface AddRecordDialogProps {
  open: boolean;
  onClose: () => void;
  recordType: ChildRecordType;
  assetId: string;
  onCreated: () => void;
}

const AddRecordDialog: React.FC<AddRecordDialogProps> = ({
  open, onClose, recordType, assetId, onCreated,
}) => {
  const { session } = useSession();
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [timestamp, setTimestamp] = useState(new Date().toISOString().slice(0, 16));
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      const defaults: Record<string, any> = {};
      for (const field of recordType.schema.fields) {
        defaults[field.name] = field.type === 'number' ? '' : '';
      }
      setFormData(defaults);
      setTimestamp(new Date().toISOString().slice(0, 16));
    }
  }, [open, recordType]);

  const handleSubmit = async () => {
    if (!session) return;
    setIsSubmitting(true);
    try {
      // Convert number fields
      const data: Record<string, any> = {};
      for (const field of recordType.schema.fields) {
        const val = formData[field.name];
        if (field.type === 'number' && val !== '' && val != null) {
          data[field.name] = parseFloat(val);
        } else if (val !== '' && val != null) {
          data[field.name] = val;
        }
      }

      const resp = await fetchWithAuth(
        `${API_URL}/assets/${assetId}/child-records`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            recordType: recordType.recordType,
            data,
            timestamp: new Date(timestamp).toISOString(),
            source: 'manual',
          }),
        },
        session,
      );

      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.message || 'Opslaan mislukt');
      }

      toast.success(`${recordType.displayName} toegevoegd`);
      onCreated();
      onClose();
    } catch (err: any) {
      toast.error(err?.message || 'Fout bij het opslaan');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{recordType.displayName} toevoegen</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Timestamp */}
          <div>
            <Label className="text-xs mb-1.5 block">Datum & tijd</Label>
            <Input
              type="datetime-local"
              value={timestamp}
              onChange={(e) => setTimestamp(e.target.value)}
              className="text-sm"
            />
          </div>

          {/* Dynamic fields from schema */}
          {recordType.schema.fields.map(field => (
            <div key={field.name}>
              <Label className="text-xs mb-1.5 block">
                {field.label}
                {field.required && <span className="text-destructive ml-0.5">*</span>}
                {field.unit && <span className="text-muted-foreground ml-1">({field.unit})</span>}
              </Label>
              {field.type === 'number' ? (
                <Input
                  type="number"
                  step="any"
                  value={formData[field.name] || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: e.target.value }))}
                  placeholder={field.unit ? `bijv. 1234 ${field.unit}` : 'Voer waarde in'}
                  className="text-sm"
                />
              ) : field.type === 'boolean' ? (
                <select
                  value={formData[field.name] || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: e.target.value === 'true' }))}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">Selecteer...</option>
                  <option value="true">Ja</option>
                  <option value="false">Nee</option>
                </select>
              ) : field.type === 'date' ? (
                <Input
                  type="date"
                  value={formData[field.name] || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: e.target.value }))}
                  className="text-sm"
                />
              ) : (
                <Input
                  type="text"
                  value={formData[field.name] || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: e.target.value }))}
                  placeholder={`Voer ${field.label.toLowerCase()} in`}
                  className="text-sm"
                />
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Annuleren
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Bezig...' : 'Opslaan'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AssetChildRecordsSection;
